import JSONViewer from '@/components/common/debug/JSONViewer';
import OutlinedAccordion, {
  OutlinedAccordionDetails,
  OutlinedAccordionSummary
} from '@/components/common/styled/OutlinedAccordion';
import { isError } from '@/util/typeGuards';
import { ContentCopy } from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Typography,
  useTheme
} from '@mui/material';
import { FC } from 'react';

export type ErrorMessage = {
  title?: string;
  message: string;
  stacktrace?: unknown;
};

type ErrorDialogProps = ErrorMessage & {
  open: boolean;
  onClose: () => void;
};

const ErrorDialog: FC<ErrorDialogProps> = (props) => {
  const theme = useTheme();

  const isJSON = (message: string) => {
    if (!props.stacktrace) return false;
    try {
      JSON.parse(message);
      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center'
        }}
      >
        <WarningIcon />
        {props.title || 'An error has occurred'}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <DialogContentText>{props.message}</DialogContentText>
        {props.stacktrace && isError(props.stacktrace) ? (
          <OutlinedAccordion>
            <OutlinedAccordionSummary>
              <Typography variant='body1' color='inherit'>
                Details
              </Typography>
            </OutlinedAccordionSummary>
            <OutlinedAccordionDetails
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'start'
              }}
            >
              {isJSON(props.stacktrace.message) ? (
                <JSONViewer src={JSON.parse(props.stacktrace.message)} />
              ) : (
                <Typography variant='subtitle1' color='inherit' fontFamily='monospace'>
                  {props.stacktrace.message}
                </Typography>
              )}
              <Button
                startIcon={<ContentCopy />}
                variant='contained'
                onClick={() => {
                  if (!isError(props.stacktrace)) return;
                  navigator.clipboard.writeText(props.stacktrace?.message + ' | ' + props.stacktrace?.stack);
                }}
              >
                Copy to clipboard
              </Button>
              <Typography
                variant='caption'
                // color="inherit"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  // bgcolor: theme.palette.grey[300],
                  // border: `1px solid ${theme.palette.grey[400]}`,
                  p: 2,
                  maxHeight: theme.spacing(50),
                  overflow: 'auto'
                }}
              >
                {props.stacktrace.stack}
              </Typography>
            </OutlinedAccordionDetails>
          </OutlinedAccordion>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorDialog;
